import { ColumnDefWithFilters, ColumnGroup } from '../types'
import { companionPendingMessage, useSimpleColumns } from '../utils'
import { useMemo } from 'react'
import { usePageInfo } from 'app/frontend/base/config/queries'
import Select from 'app/frontend/base/components/Select'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import {
  useMinimumLevelFilter,
  useWeeklyDungeonTotalColumn,
} from './dungeons_weekly'
import { useMissingColumn } from './enchantments'
import {
  equippedGearGroup,
  ilvlDisplayOptions,
  SlotDisplay,
  slotDisplayOptions,
} from './gear_overview'
import {
  gameVersionConfig,
  gameVersionHas,
} from 'app/frontend/types/custom/GameVersion'
import { useAudit } from '../context'
import { valueToCutoff } from './vault'
import { groupBy } from 'lodash'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import UpgradeTrack, {
  colorByTrack,
} from 'app/frontend/types/custom/UpgradeTrack'
import {
  formatUnixAge,
  isUnixAtLeastDaysOld,
} from 'app/frontend/base/helpers/relativeAge'

const trackPriority: UpgradeTrack[] = ['Myth', 'Hero', 'Champion', 'Veteran']

export const useCompanionSummaryColumns = (): ColumnDefWithFilters => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const maxLevel = gameVersionConfig(gameVersion).max_level

  const availableSlotDisplayOptions = slotDisplayOptions.filter(
    (option) =>
      option.value !== 'track' || gameVersionHas('upgrade_tracks', gameVersion),
  )
  const { value: slotDisplay, setValue: setSlotDisplay } =
    useAuditFilterState<SlotDisplay>(
      'slotDisplay',
      'ilvl',
      availableSlotDisplayOptions.map((o) => o.value),
    )

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'character',
        header: '',
        rankFields: null,
        subColumns: [
          {
            id: 'synced_age',
            header: 'Age',
            field: 'synced_at',
            numeric: true,
            cellWrapper: (value) => (
              <span
                className={
                  isUnixAtLeastDaysOld(value, 7)
                    ? 'text-warning-foreground'
                    : ''
                }
              >
                {formatUnixAge(value)}
              </span>
            ),
          },
          {
            id: 'level',
            header: 'Level',
            numeric: true,
            accessor: (member) => member.characterReference.level ?? 0,
            cellWrapper: (value) => (
              <span
                className={
                  Number(value) >= maxLevel ? 'text-success-foreground' : ''
                }
              >
                {value || '-'}
              </span>
            ),
          },
          {
            id: 'total_ilvl',
            header: 'Item level',
            numeric: true,
            field: 'ilvl',
            cellWrapper: (value) => (
              <span>{Number(value) ? Number(value).toFixed(2) : '-'}</span>
            ),
          },
        ],
      },
      {
        id: 'professions',
        header: 'Professions',
        rankFields: null,
        subColumns: [
          { index: 1, header: 'First' },
          { index: 2, header: 'Second' },
        ].map(({ index, header }) => ({
          id: `profession_${index}`,
          header,
          numeric: true,
          field: `profession_${index}_rank`,
          cellWrapper: (value, member) => {
            const name = member.data?.[`profession_${index}_name`]
            if (!name) return null

            const maxRank = member.data?.[`profession_${index}_max_rank`]
            return (
              <span className='whitespace-nowrap'>
                {name}{' '}
                <span
                  className={
                    Number(value) >= Number(maxRank)
                      ? 'text-success-foreground'
                      : 'text-warning-foreground'
                  }
                >
                  {value}
                </span>
                <span className='text-muted-foreground'> / {maxRank}</span>
              </span>
            )
          },
        })),
      },
      equippedGearGroup(gameVersion, slotDisplay),
    ],
    [gameVersion, maxLevel, slotDisplay],
  )

  const columns = useSimpleColumns(collectionGroups, [slotDisplay])

  return {
    columns,
    filters: [
      <AuditFilter label='Gear display' key='slotDisplay'>
        <Select
          className='w-40'
          value={slotDisplay}
          setValue={(value) => setSlotDisplay(value as SlotDisplay)}
          options={availableSlotDisplayOptions}
        />
      </AuditFilter>,
    ],
    initialSortBy: [{ id: 'total_ilvl', desc: true }],
    isPending: (member) => !member.data?.synced_at,
    pendingDataMessage: companionPendingMessage,
    pendingDataStartColumnIndex: 3,
  }
}

export const useRetailSummaryColumns = (): ColumnDefWithFilters => {
  const { visibleMembers, weeksAgo } = useAudit()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const { value: totalIlvlDisplay, setValue: setTotalIlvlDisplay } =
    useAuditFilterState<'best' | 'current'>(
      'totalIlvlDisplay',
      'best',
      ilvlDisplayOptions.map((o) => o.value),
    )

  const { minimumLevel } = useMinimumLevelFilter()
  const { missingColumn } = useMissingColumn('Enchantments', 'border-l')

  const { weeklyDungeonTotalColumn } = useWeeklyDungeonTotalColumn(
    'Dungeons done',
    'Amount',
    minimumLevel,
  )

  const vaultSlotsByTrack = useMemo(() => {
    return Object.fromEntries(
      visibleMembers.map((member) => {
        const value = Array.from({ length: 9 })
          .map((_, index) =>
            weeksAgo === 0
              ? member.data?.[`great_vault_slot_${index + 1}`]
              : member.data?.[
                  `historical_great_vault_slot_${index + 1}`
                ]?.split('|')[weeksAgo - 1],
          )
          .filter(Boolean)
          .map((value) => valueToCutoff(currentSeason, Number(value)))

        return [member.characterReference.id, groupBy(value)]
      }),
    )
  }, [currentSeason, visibleMembers, weeksAgo])

  const vaultRankbyId = useMemo(() => {
    if (!visibleMembers.some((row) => row.data)) {
      return undefined
    }

    const withCounts = visibleMembers.map((row) => ({
      id: row.id,
      counts: trackPriority.map(
        (track) =>
          vaultSlotsByTrack[row.characterReference.id]?.[track]?.length ?? 0,
      ),
    }))

    const sorted = [...withCounts].sort((a, b) => {
      for (let i = 0; i < a.counts.length; i++) {
        if (a.counts[i] !== b.counts[i]) return b.counts[i] - a.counts[i]
      }
      return 0
    })

    return Object.fromEntries(
      withCounts.map((row) => {
        const rank =
          sorted.findIndex((r) =>
            r.counts.every((c, i) => c === row.counts[i]),
          ) + 1
        return [row.id, rank]
      }),
    )
  }, [visibleMembers, vaultSlotsByTrack, weeksAgo])

  const {
    data: { gameVersion },
  } = usePageInfo()

  const colorsByCharacter = useMemo(() => {
    return Object.fromEntries(
      visibleMembers.map((member) => [
        member.characterReference.id,
        member.mythicRatingColor,
      ]),
    )
  }, [visibleMembers])

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'item_level',
        header: '',
        subColumns: [
          {
            id: 'total_ilvl',
            header: 'Item level',
            numeric: true,
            field:
              totalIlvlDisplay === 'best'
                ? 'highest_ilvl_ever_equipped'
                : 'ilvl',
            cellWrapper: (value) => {
              return <span>{value ? Number(value).toFixed(2) : ''}</span>
            },
          },
          {
            id: 'omnium_folio',
            header: 'Omnium Folio',
            field: 'folio_amount',
            size: 90,
            cellWrapper: (value) => {
              return (
                <span
                  className={
                    value === '0 / 5'
                      ? 'text-destructive-foreground'
                      : value === '5 / 5'
                      ? 'text-success-foreground'
                      : 'text-warning-foreground'
                  }
                >
                  {value}
                </span>
              )
            },
          },
        ],
      },
      {
        id: 'vault',
        header: 'Vault slots',
        rankById: vaultRankbyId,
        subColumns: trackPriority.map((track) => ({
          id: `vault_${track}`,
          header: () => (
            <Tooltip>
              <TooltipTrigger>{track[0]}</TooltipTrigger>
              <TooltipContent>{track} track</TooltipContent>
            </Tooltip>
          ),
          cellWrapper: (_, member) => {
            const count =
              vaultSlotsByTrack[member.characterReference.id]?.[track]
                ?.length ?? 0

            return (
              <span className={colorByTrack[track]}>
                {count === 0 ? '' : count}
              </span>
            )
          },
        })),
      },
      {
        id: 'mythic_rating',
        header: 'Mythic+ rating',
        meta: {
          className: 'border-r',
        },
        subColumns: [
          {
            id: 'total_rating',
            header: 'Rating',
            field: 'm+_score',
            meta: {
              className: 'border-r',
            },
            cellWrapper: (value, member) => (
              <span
                style={{
                  color:
                    colorsByCharacter[member.characterReference.id]?.overall ||
                    'inherit',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
    ],
    [colorsByCharacter, gameVersion, totalIlvlDisplay, weeksAgo],
  )

  const simpleColumns = useSimpleColumns(collectionGroups, [
    totalIlvlDisplay,
    weeksAgo,
  ])

  return {
    columns: [...simpleColumns, weeklyDungeonTotalColumn, missingColumn],
    filters: [
      <AuditFilter label='Shown item level' key='totalIlvlDisplay'>
        <Select
          className='w-40'
          value={totalIlvlDisplay}
          setValue={(value) => setTotalIlvlDisplay(value as 'best' | 'current')}
          options={ilvlDisplayOptions}
        />
      </AuditFilter>,
    ],
    hasHistory: true,
    initialSortBy: [{ id: 'total_ilvl', desc: true }],
  }
}
